/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement, useEffect, useRef } from 'react';
import { AdvancedMarker, APIProvider, Map, useMap } from '@vis.gl/react-google-maps';

export type TrackPoint = { lat: number; lng: number; t: number };
export type Track = { key: string; label: string; color: string; points: TrackPoint[] };

type Props = { tracks: Track[]; version: number };

/**
 * Draws each phone's GPS path as a polyline. Polylines are created once per
 * track and only have their path replaced on update — no React re-mounts of
 * map overlays at stream rates. The view auto-fits until the user pans or
 * zooms, then stays where they put it.
 */
function Tracks({ tracks, version }: Props): ReactElement | null {
  const map = useMap();
  const lines = useRef<Record<string, google.maps.Polyline>>({});
  const userMoved = useRef(false);
  const fitting = useRef(false);

  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener('dragstart', () => {
      userMoved.current = true;
    });
    const zoom = map.addListener('zoom_changed', () => {
      if (!fitting.current) userMoved.current = true;
    });
    return () => {
      listener.remove();
      zoom.remove();
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    const live = new Set(tracks.map((t) => t.key));
    Object.keys(lines.current).forEach((key) => {
      if (!live.has(key)) {
        lines.current[key].setMap(null);
        delete lines.current[key];
      }
    });
    const bounds = new window.google.maps.LatLngBounds();
    let any = false;
    tracks.forEach((t) => {
      const path = t.points.map((p) => ({ lat: p.lat, lng: p.lng }));
      let line = lines.current[t.key];
      if (!line) {
        line = new window.google.maps.Polyline({
          map,
          strokeColor: t.color,
          strokeOpacity: 0.9,
          strokeWeight: 3
        });
        lines.current[t.key] = line;
      }
      line.setPath(path);
      path.forEach((p) => {
        bounds.extend(p);
        any = true;
      });
    });
    if (any && !userMoved.current) {
      fitting.current = true;
      map.fitBounds(bounds, 48);
      window.setTimeout(() => {
        fitting.current = false;
      }, 300);
    }
  }, [map, tracks, version]);

  useEffect(
    () => () => {
      Object.values(lines.current).forEach((l) => l.setMap(null));
      lines.current = {};
    },
    []
  );

  return (
    <>
      {tracks.map((t) => {
        const last = t.points[t.points.length - 1];
        if (!last) return null;
        return (
          <AdvancedMarker key={t.key} position={{ lat: last.lat, lng: last.lng }} title={t.label}>
            <span
              className="block h-3.5 w-3.5 rounded-full border-2 border-white shadow"
              style={{ backgroundColor: t.color }}
            />
          </AdvancedMarker>
        );
      })}
    </>
  );
}

export default function LiveTrackMap({ tracks, version }: Props): ReactElement {
  const hasPoints = tracks.some((t) => t.points.length > 0);
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-gray-800 bg-container-500 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-gray-100">Phone location</h3>
        <span className="text-xs text-gray-500">GPS track in window</span>
      </div>
      <div className="relative h-80 overflow-hidden rounded-md">
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
          <Map
            mapId="2710c5f22fa0158b"
            defaultCenter={{ lat: 44.67, lng: -74.98 }}
            defaultZoom={14}
            gestureHandling="greedy"
            disableDefaultUI
            zoomControl
          >
            <Tracks tracks={tracks} version={version} />
          </Map>
        </APIProvider>
        {!hasPoints && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background-500/60 text-sm text-gray-400">
            No GPS position in this window yet
          </div>
        )}
      </div>
    </div>
  );
}
